import React, { useState, useMemo, useEffect } from 'react';
import WidgetPopUp from './WidgetPopUp';
import OptionButton from './OptionButton';
import folderIcon from "../../pics/icons/folder.png";
import defaultBackground from '../../pics/backgrounds/happyTavern.png';
import checkPic from '../../pics/icons/check.png'

// Helper to build a nested folder structure from folder paths
function buildNestedFolders(folders) {
    const root = {};
    folders.forEach(folderPath => {
        const parts = folderPath.split('/');
        let node = root;
        parts.forEach((part, idx) => {
            if (!node[part]) node[part] = { __children: {}, __isLeaf: false, __fullPath: parts.slice(0, idx + 1).join('/') };
            if (idx === parts.length - 1) node[part].__isLeaf = true;
            node = node[part].__children;
        });
    });
    return root;
}

function FolderTree({
    node,
    depth = 0,
    selectedFolder,
    setSelectedFolder,
    creatingNewFolder,
    setCreatingNewFolder,
    setFolderName,
    onHover,
    onLeave,
    collapsedFolders,
    setCollapsedFolders
}) {
    return Object.entries(node).sort(([a], [b]) => a.localeCompare(b)).map(([folder, data]) => {
        const folderKey = `${depth}-${data.__fullPath}`;
        const isCollapsed = collapsedFolders[folderKey];
        const isSelected = selectedFolder === data.__fullPath && !creatingNewFolder;

        return (
            <React.Fragment key={data.__fullPath}>
                <div
                    className={`folderListItem${isSelected ? " folderSelected" : ""}`}
                    style={{
                        padding: "6px 10px",
                        margin: "2px 0",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: isSelected ? "#dcd9d0" : undefined,
                        border: isSelected ? "1px solid #822000" : "1px solid transparent",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: depth === 0 ? 700 : 500,
                        fontSize: depth === 0 ? "1.04rem" : "1rem",
                        marginLeft: depth * 12 + 5,
                        marginRight: depth * 12 + 20
                    }}
                    onMouseEnter={e => onHover && onHover(e, isSelected)}
                    onMouseLeave={e => onLeave && onLeave(e, isSelected)}
                >
                    {Object.keys(data.__children).length > 0 && (
                        <span
                            style={{
                                marginRight: 8,
                                userSelect: "none",
                                fontSize: "1.1em",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                width: 16,
                                height: 16
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                setCollapsedFolders(prev => ({
                                    ...prev,
                                    [folderKey]: !isCollapsed
                                }));
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                style={{
                                    display: "block",
                                    transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                                    transition: "transform 0.15s"
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <polyline
                                    points="4,3 9,7 4,11"
                                    fill="none"
                                    stroke="#822000"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    )}
                    <span
                        style={{ flex: 1}}
                        onClick={() => {
                            setSelectedFolder(data.__fullPath);
                            setCreatingNewFolder(false);
                            setFolderName(data.__fullPath);
                        }}
                    >
                        {folder}
                    </span>
                </div>
                {!isCollapsed && Object.keys(data.__children).length > 0 && (
                    <FolderTree
                        node={data.__children}
                        depth={depth + 1}
                        selectedFolder={selectedFolder}
                        setSelectedFolder={setSelectedFolder}
                        creatingNewFolder={creatingNewFolder}
                        setCreatingNewFolder={setCreatingNewFolder}
                        setFolderName={setFolderName}
                        onHover={onHover}
                        onLeave={onLeave}
                        collapsedFolders={collapsedFolders}
                        setCollapsedFolders={setCollapsedFolders}
                    />
                )}
            </React.Fragment>
        );
    });
}

const SaveToFolderWidget = ({currentEncounter, setCurrentEncounter, isBackgroundImage, backgroundUrl, savedEncounters, socket}) => {
    const [folderName, setFolderName] = useState(currentEncounter.folder_dir || '');
    const [saveStatus, setSaveStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(currentEncounter.folder_dir || '');
    const [creatingNewFolder, setCreatingNewFolder] = useState(false);
    const [collapsedFolders, setCollapsedFolders] = useState({});

    // Maintain folders in local state so we can add/remove dynamically
    const initialFolders = useMemo(() => {
        if (!savedEncounters || !Array.isArray(savedEncounters)) return [];
        const folderSet = new Set();
        savedEncounters.forEach(enc => {
            if (enc.folder_dir) folderSet.add(enc.folder_dir);
        });
        return Array.from(folderSet);
    }, [savedEncounters]);

    const [folders, setFolders] = useState(initialFolders);

    useEffect(() => {
        setTimeout(() => {
            setSaveStatus("");
        }, 3500)
    }, [saveStatus]);

    // Keep folders in sync if savedEncounters changes
    useEffect(() => {
        setFolders(initialFolders);
    }, [initialFolders]);
    
    useEffect(() => {
        if(socket) {
            socket.on("saveEncounterToFolderResponse", (success) =>  {
                setSaveStatus(success)
                setSaving(false)
            })
        }
    }, [socket]);

    // Save to selected folder
    const handleSaveToFolder = async () => {
        setSaving(true);
        setSaveStatus(null);

        const folderToSave = creatingNewFolder ? folderName : selectedFolder;
        await socket.emit("saveEncounterToFolder", currentEncounter, folderToSave);
        setCurrentEncounter({...currentEncounter, folder_dir: folderToSave})
        if (creatingNewFolder && folderName && !folders.includes(folderName)) {
            setFolders(prev => [...prev, folderName]);
            setSelectedFolder(folderName);
            setCreatingNewFolder(false);
            setFolderName('');
        }
    };

    // Delete selected folder
    const handleDeleteFromFolder = () => {
        if (!selectedFolder) return;
       
        setSelectedFolder(null);
        socket.emit("saveEncounterToFolder", currentEncounter, null);
        setCurrentEncounter({...currentEncounter, folder_dir: null})

    }

    // Hover handlers for folder highlight
    const handleHover = (e, isSelected) => {
        if (!isSelected) {
            e.currentTarget.style.background = "#f3e7d6";
            e.currentTarget.style.border = "1px solid #c7a97a";
        }
    };
    const handleLeave = (e, isSelected) => {
        if (!isSelected) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.border = "1px solid transparent";
        }
    };

    // Build nested folder structure for display
    const nestedFolders = useMemo(() => buildNestedFolders(folders), [folders]);

    return (
        <div>
            <WidgetPopUp 
                triggerClass='folderOptionsWrapper'
                trigger={
                    <OptionButton src={folderIcon} message={"Add/Move to Folder"} imgClassName='removeMargin' />
                }
                popUp={
                    <div
                        className='saveFolderWidget background'
                        style={{
                            backgroundImage: isBackgroundImage ? `url(${backgroundUrl})` : `url(${defaultBackground})`,
                            padding: 20,
                            paddingTop: 2,
                            borderRadius: 8,
                            minHeight: creatingNewFolder ? 400 : 220, // Animate height for new folder
                            transition: "min-height 0.3s cubic-bezier(.4,1,.6,1)", // Smooth animation
                        }}
                    >
                        <div className="folderWidgetFlag"/>
                        <div style={{margin: 10, padding: 10, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', background: "rgba(104, 104, 104, 0.51)"}}>
                            <div
                                style={{
                                    fontWeight: 700,
                                    fontSize: "1.15rem",
                                    color: "#822000",
                                    letterSpacing: "0.5px",
                                    textShadow: "0 1px 2px #fff8, 0 0px 1px #82200022",
                                    background: "rgba(196, 196, 196, 0.95)",
                                    textAlign: "center",
                                    width: "100%",
                                    padding: "5px 0px",
                                }}
                            >
                                Choose Folder for:
                                <div style={{fontSize: 18, color: "black"}}>
                                    {currentEncounter.encounterName}
                                </div>
                                <div style={{fontSize: 12, color: 'GrayText'}}>
                                    <i>Folders only appear if an encounter exists within</i>
                                </div>
                            </div>
                            <div
                                style={{
                                    height: 'fit-content',
                                    width: "100%",
                                    maxHeight: 200,
                                    overflowY: "scroll",
                                    marginBottom: 10,
                                    background: "rgba(207, 207, 207, 0.94)",
                                    borderBottomLeftRadius: 6,
                                    borderBottomRightRadius: 6,
                                }}
                            >
                                {currentEncounter.folder_dir !== null && 
                                    <div
                                        key={currentEncounter.folder_dir + "current"}
                                        style={{
                                            padding: "6px 10px",
                                            margin: "2px 0",
                                            borderBottom: "2px solid black"
                                        }}
                                    >
                                        <i>Current: <b>{currentEncounter.folder_dir}</b></i>
                                    </div>
                                }
                                <FolderTree
                                    node={nestedFolders}
                                    selectedFolder={selectedFolder}
                                    setSelectedFolder={setSelectedFolder}
                                    creatingNewFolder={creatingNewFolder}
                                    setCreatingNewFolder={setCreatingNewFolder}
                                    setFolderName={setFolderName}
                                    onHover={handleHover}
                                    onLeave={handleLeave}
                                    collapsedFolders={collapsedFolders}
                                    setCollapsedFolders={setCollapsedFolders}
                                />
                            </div>
                            {/* Save button comes before New Folder */}
                            {!creatingNewFolder && 
                                <div style={{display: 'flex', width: "100%", justifyContent: 'center'}}>
                                    <button
                                    
                                        style={{
                                            width: "30%",
                                            padding: '8px 10px',
                                            margin: '5px',
                                            background: "#eee",
                                            border: creatingNewFolder ? "1px solid #822000" : "1px solid #ccc",
                                            cursor: currentEncounter.folder_dir === null || saving ? "auto" : "pointer"
                                        }}
                                        onClick={handleSaveToFolder}
                                        disabled={
                                            saving ||
                                            (!creatingNewFolder && !selectedFolder) ||
                                            (creatingNewFolder && !folderName)
                                        }
                                    >
                                        Move To
                                    </button>
                                       
                                    {selectedFolder && 
                                        <div
                                            style={{
                                                padding: '6px 10px',
                                                margin: '5px',
                                                borderRadius: '4px',
                                                border: '1px solid rgb(130, 32, 0)',
                                                background: 'rgb(220, 217, 208)'
                                            }}
                                        >
                                            <b>{selectedFolder}</b>
                                        </div>
                                    }
                                </div>
                            }
                            <div style={{display: "flex", width: '90%', gap: "10px"}}>
                                <button
                                    style={{
                                        width: "50%",
                                        marginTop: 8,
                                        background: "#eee",
                                        border: creatingNewFolder ? "1px solid #822000" : "1px solid #ccc",
                                        cursor: currentEncounter.folder_dir === null || saving ? "auto" : "pointer"
                                    }}
                                    onClick={handleDeleteFromFolder}
                                    disabled={currentEncounter.folder_dir === null || saving}
                                >
                                    Remove From Folder
                                </button>
                                <button
                                    style={{
                                        width: "50%",
                                        marginTop: 8,
                                        background: "#eee",
                                        border: creatingNewFolder ? "1px solid #822000" : "1px solid #ccc",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => {
                                        setCreatingNewFolder(true);
                                        setSelectedFolder(null);
                                    }}
                                    disabled={creatingNewFolder}
                                >
                                    Create New Folder
                                </button>
                            </div>
                            {creatingNewFolder && (
                                <>
                                    <div style={{marginTop: 6, display: "flex", alignItems: "center"}}>
                                        <input
                                            type="text"
                                            placeholder="New Folder Name"
                                            value={folderName}
                                            onChange={e => setFolderName(e.target.value)}
                                            style={{width: "70%", height: "70%"}}
                                            disabled={saving}
                                        />
                                        <button
                                            style={{
                                                marginLeft: 8,
                                                padding: "2px 8px",
                                                background: "#eee",
                                                border: "1px solid #aaa",
                                                borderRadius: 4,
                                                cursor: "pointer"
                                            }}
                                            onClick={handleSaveToFolder}
                                            disabled={
                                                saving ||
                                                (!creatingNewFolder && !selectedFolder) ||
                                                (creatingNewFolder && !folderName)
                                            }
                                        >
                                            Save
                                        </button>
                                        <button
                                            style={{
                                                marginLeft: 8,
                                                padding: "2px 8px",
                                                background: "#eee",
                                                border: "1px solid #aaa",
                                                borderRadius: 4,
                                                cursor: "pointer"
                                            }}
                                            onClick={() => {
                                                setCreatingNewFolder(false);
                                                setFolderName('');
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>

                                    </div>
                                    
                                    <div
                                        style={{
                                            height: '15%',
                                            width: "80%",
                                            maxHeight: 290,
                                            overflowY: "auto",
                                            margin: 5,
                                            background: "rgba(226, 226, 226, 0.9)",
                                            borderRadius: 6,
                                            textAlign: "center"
                                        }}
                                    >
                                        <div>Create sub folders by adding a '/'</div>
                                        <i>i.e. "Folder/SubFolder"</i>
                                    </div>
                                </>
                            )}
                            <div style={{textAlign: "center", marginTop: 20}}>
                                {(saveStatus && !saving) && <img alt='saved' style={{width: '20px', height: '20px'}} src={checkPic}/> }
                            </div>
                        </div>
                    </div>
                }
                side='bottom'
                alignOffset={-16}
                sideOffset={15}
            />
        </div>
    );
};

export default SaveToFolderWidget;